import {
  DropDownList,
  type DropDownListChangeEvent,
  type DropDownListHandle,
} from "@progress/kendo-react-dropdowns";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import React from "react";

interface RoleOption {
  value: number;
  text: string;
}

interface DropdownInputProps {
  id?: string;
  value?: number | string | null;
  onChange: (val: number | string) => void;
  onValidate?: (isValid: boolean) => void;
  label?: string;
  data: RoleOption[] | null | undefined;
  required?: boolean;
  validationMessage?: string;
  className?: string;
  placeholder?: string;
  onClick?: () => void;
  iconSrc?: string;
  disable?: boolean;
  triggerValidation?: boolean;
}

const SecuritySelectDropdown: React.FC<DropdownInputProps> = ({
  id = "security-dropdown",
  value,
  onChange,
  onValidate,
  data = [],
  required = false,
  className = "",
  placeholder = "",
  disable = false,
  triggerValidation,
  validationMessage = "Please select a value.",
}) => {
  const { t } = useTranslation();

  const options = Array.isArray(data) ? data : [];

  const dropDownRef = useRef<DropDownListHandle>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [touched, setTouched] = useState(false);
  const [localValidationMessage, setLocalValidationMessage] = useState("");

  const selectedItem = options.find((opt) => opt.value === value) ?? null;

  const showFloatingLabel = isFocused || !!value;

  const validateSelection = (val: number | string | null | undefined) => {
    if (required && (val === null || val === undefined || val === "")) {
      setLocalValidationMessage(validationMessage);
      return false;
    }

    setLocalValidationMessage("");
    return true;
  };

  const updateValidation = (val: number | string | null | undefined) => {
    const valid = validateSelection(val);

    setIsValid(valid);
    onValidate?.(valid);
  };

  const handleChange = (e: DropDownListChangeEvent) => {
    const selected = e.target.value as RoleOption | null;

    if (selected) {
      onChange(selected.value);
    }

    setTouched(true);
    updateValidation(selected?.value);
  };

  const handleBlur = () => {
    setTouched(true);
    setIsFocused(false);

    updateValidation(value);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(value);
    }
  }, [triggerValidation, value]);



  const handleLabelClick = () => {
    if (!disable) {
      dropDownRef.current?.focus?.();
    }
  };

  return (
    <div className="relative gx-dropdown securityquestions">
      <div
        className={`datepicker-field-wrapper ${className} ${
          !isValid && showFloatingLabel ? "invalid" : ""
        }`}
      >
        <label
          id={`${id}-label`}
          className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
          onClick={handleLabelClick}
        >
          {t(placeholder)}

          {required && <span className="text-red-500 ms-1">*</span>}
        </label>

        <div
          className={`gx-dropdown-container ${
            !isValid && touched ? "border-red-500 border-2" : ""
          }`}
          aria-invalid={!isValid}
          aria-describedby={!isValid && touched ? `${id}-error` : undefined}
        >
          <DropDownList
            id={id}
            ref={dropDownRef}
            data={options}
            value={selectedItem}
            textField="text"
            dataItemKey="value"
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disable}
            filterable={false}
            ariaLabelledBy={`${id}-label`}
            className={selectedItem ? "gx-filled" : "gx-placeholder"}
          />
        </div>

        {touched && localValidationMessage && (
          <div
            className="gx_input_validationmessage gx-font-regular"
            role="alert"
          >
            {localValidationMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecuritySelectDropdown;
